import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Grid, Modal, Space, Tabs, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import api from '../../../api/client';
import AgendaCalendarioMensual from './AgendaCalendarioMensual';
import AgendaCalendarioSemanal from './AgendaCalendarioSemanal';
import AgendaCalendarioDiario from './AgendaCalendarioDiario';
import AgendaListado from './AgendaListado';
import AgendaReunionForm from './AgendaReunionForm';
import AgendaReunionDetalle from './AgendaReunionDetalle';
import AgendaEstadoLeyenda from './AgendaEstadoLeyenda';

dayjs.extend(isoWeek);

const { Title } = Typography;

const AgendaReuniones = () => {
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [mainTab, setMainTab] = useState('calendario');
  const [calTab, setCalTab] = useState('mensual');
  const [calVistaModo, setCalVistaModo] = useState('month');
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [reuniones, setReuniones] = useState([]);
  const [loadingCal, setLoadingCal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [initialFecha, setInitialFecha] = useState(null);

  const [detalleOpen, setDetalleOpen] = useState(false);
  const [detalleReunion, setDetalleReunion] = useState(null);

  const getRange = useCallback(() => {
    if (calTab === 'mensual') {
      if (calVistaModo === 'year') {
        return {
          desde: currentDate.startOf('year').format('YYYY-MM-DD'),
          hasta: currentDate.endOf('year').format('YYYY-MM-DD'),
        };
      }
      const start = currentDate.startOf('month').subtract(7, 'day');
      const end = currentDate.endOf('month').add(7, 'day');
      return { desde: start.format('YYYY-MM-DD'), hasta: end.format('YYYY-MM-DD') };
    }
    if (calTab === 'semanal') {
      const start = currentDate.startOf('isoWeek');
      const end = currentDate.endOf('isoWeek');
      return { desde: start.format('YYYY-MM-DD'), hasta: end.format('YYYY-MM-DD') };
    }
    const key = currentDate.format('YYYY-MM-DD');
    return { desde: key, hasta: key };
  }, [calTab, calVistaModo, currentDate]);

  const loadCalendario = useCallback(async () => {
    setLoadingCal(true);
    try {
      const { desde, hasta } = getRange();
      const { data } = await api.get('/agenda-reuniones/calendario', { params: { desde, hasta } });
      setReuniones(data.items || []);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar calendario');
    } finally {
      setLoadingCal(false);
    }
  }, [getRange]);

  useEffect(() => {
    if (mainTab === 'calendario') {
      loadCalendario();
    }
  }, [mainTab, loadCalendario, refreshKey]);

  const refreshAll = () => setRefreshKey((k) => k + 1);

  const openCreate = (fecha) => {
    setEditing(null);
    setInitialFecha(fecha || dayjs().format('YYYY-MM-DD'));
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setDetalleOpen(false);
    setEditing(row);
    setInitialFecha(null);
    setFormOpen(true);
  };

  const openView = (row) => {
    setDetalleReunion(row);
    setDetalleOpen(true);
  };

  const handleSubmit = async (values) => {
    const payload = {
      fecha: values.fecha,
      horaInicio: values.horaInicio || null,
      horaFin: values.horaFin || null,
      cliente: values.cliente,
      contacto: values.contacto || null,
      telefono: values.telefono || null,
      email: values.email || null,
      localidad: values.localidad || null,
      producto: values.producto || null,
      comentarios: values.comentarios || null,
      estado: values.estado || 'Pendiente',
    };

    try {
      if (editing) {
        await api.put(`/agenda-reuniones/${editing.id}`, payload);
        message.success('Reunión actualizada');
      } else {
        await api.post('/agenda-reuniones', payload);
        message.success('Reunión creada');
      }
      setFormOpen(false);
      refreshAll();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al guardar');
      throw e;
    }
  };

  const handleDelete = (row) => {
    Modal.confirm({
      title: 'Eliminar reunión',
      content: `¿Eliminar reunión con ${row.cliente}?`,
      okText: 'Eliminar',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/agenda-reuniones/${row.id}`);
          message.success('Reunión eliminada');
          setDetalleOpen(false);
          refreshAll();
        } catch (e) {
          message.error(e?.response?.data?.message || 'Error al eliminar');
        }
      },
    });
  };

  const handleMover = async (reunion, { fecha, horaInicio, horaFin }) => {
    try {
      await api.patch(`/agenda-reuniones/${reunion.id}/mover`, { fecha, horaInicio, horaFin });
      message.success('Reunión reprogramada');
      refreshAll();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al mover reunión');
    }
  };

  const handleSelectDay = (fecha) => {
    const tieneReuniones = reuniones.some((r) => String(r.fecha).slice(0, 10) === fecha);
    if (!tieneReuniones) {
      openCreate(fecha);
    }
  };

  const calendarioContent = (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Tabs
          activeKey={calTab}
          onChange={setCalTab}
          size="small"
          items={[
            { key: 'mensual', label: 'Mensual' },
            { key: 'semanal', label: 'Semanal' },
            { key: 'diaria', label: 'Diaria' },
          ]}
        />
        <AgendaEstadoLeyenda />
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => openCreate()}>
          Nueva reunión
        </Button>
      </div>

      <div style={{ opacity: loadingCal ? 0.6 : 1, pointerEvents: loadingCal ? 'none' : 'auto' }}>
        {calTab === 'mensual' && (
          <AgendaCalendarioMensual
            reuniones={reuniones}
            currentDate={currentDate}
            onDateChange={setCurrentDate}
            onModoChange={setCalVistaModo}
            onSelectReunion={openView}
            onSelectDay={handleSelectDay}
          />
        )}
        {calTab === 'semanal' && (
          <AgendaCalendarioSemanal
            reuniones={reuniones}
            currentDate={currentDate}
            onDateChange={setCurrentDate}
            onSelectReunion={openView}
            onMover={handleMover}
          />
        )}
        {calTab === 'diaria' && (
          <AgendaCalendarioDiario
            reuniones={reuniones}
            currentDate={currentDate}
            onDateChange={setCurrentDate}
            onSelectReunion={openView}
            onCreate={openCreate}
          />
        )}
      </div>
    </div>
  );

  return (
    <Card
      style={{ background: '#ffffff', borderColor: '#d8e8dc', maxWidth: '100%' }}
      styles={{ body: { padding: isNarrow ? 12 : 16 } }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Title level={5} style={{ margin: 0, color: '#198039' }}>
          Agenda de Reuniones
        </Title>

        <Tabs
          activeKey={mainTab}
          onChange={setMainTab}
          items={[
            { key: 'calendario', label: 'Calendario', children: calendarioContent },
            {
              key: 'listado',
              label: 'Listado',
              children: (
                <AgendaListado
                  refreshKey={refreshKey}
                  onEdit={openEdit}
                  onView={openView}
                  onCreate={openCreate}
                />
              ),
            },
          ]}
        />
      </Space>

      <AgendaReunionForm
        open={formOpen}
        editing={editing}
        initialFecha={initialFecha}
        onCancel={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        isNarrow={isNarrow}
      />

      <AgendaReunionDetalle
        reunion={detalleReunion}
        open={detalleOpen}
        onClose={() => setDetalleOpen(false)}
        onEdit={openEdit}
        onDelete={handleDelete}
      />
    </Card>
  );
};

export default AgendaReuniones;
